import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface CSVUploadFormProps {
  campaignId: string;
  companyId: string;
  onComplete: (data: any) => void;
  onBack?: () => void;
}

export function CSVUploadForm({ campaignId, companyId, onComplete, onBack }: CSVUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [userCompanyId, setUserCompanyId] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    // Fetch user's company_id if not provided
    const fetchUserCompanyId = async () => {
      if (companyId) {
        setUserCompanyId(companyId);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        
        if (userData?.company_id) {
          setUserCompanyId(userData.company_id);
        } else {
          console.error('User has no company_id set');
        }
      }
    };

    fetchUserCompanyId();
  }, [companyId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      if (selectedFile.size > 50 * 1024 * 1024) { // 50MB limit
        alert('File size must be less than 50MB');
        return;
      }
      setFile(selectedFile);
      previewCSV(selectedFile);
    }
  };

  const previewCSV = async (file: File) => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const previewRows = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim());
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index] || '';
          return obj;
        }, {} as any);
      });
      
      setPreview(previewRows);
      setRowCount(lines.length - 1); // Exclude header row
    } catch (error) {
      console.error('Error previewing CSV:', error);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file');
      return;
    }

    if (!userCompanyId) {
      alert('Unable to determine company. Please ensure your profile is complete.');
      return;
    }

    setUploading(true);
    try {
      // Generate unique file path
      const timestamp = new Date().getTime();
      const fileName = `${timestamp}_${file.name}`;
      const filePath = `companies/${userCompanyId}/csv/${fileName}`;

      console.log('Uploading to path:', filePath);
      console.log('Company ID:', userCompanyId);

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('lead-uploads')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Process the upload via Edge Function
      const response = await supabase.functions.invoke('process-csv-upload', {
        body: {
          campaign_id: campaignId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          row_count: rowCount,
          preview_data: preview,
          column_mapping: preview.length > 0 ? Object.keys(preview[0]) : []
        }
      });

      if (response.data?.success) {
        // Set success state to show banner
        setUploadSuccess(true);
        
        // Create notification for CSRs
        await createCSRNotification(campaignId, file.name, rowCount);
        
        // Call the completion handler
        onComplete({
          type: 'csv_upload',
          data: response.data.data
        });
      } else {
        throw new Error(response.data?.error || 'Failed to process CSV upload');
      }
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      alert(error.message || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const createCSRNotification = async (campaignId: string, fileName: string, leadCount: number) => {
    try {
      console.log('Creating CSR notification for campaign:', campaignId);
      
      // Get campaign details for notification
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('name, company_id')
        .eq('id', campaignId)
        .single();

      if (campaignError) {
        console.error('Error fetching campaign:', campaignError);
        return;
      }

      if (!campaign) {
        console.log('No campaign found with ID:', campaignId);
        return;
      }

      console.log('Campaign details:', campaign);

      // Get assigned CSRs for this company
      const { data: csrs, error: csrError } = await supabase
        .from('company_csrs')
        .select('csr_id')
        .eq('company_id', campaign.company_id);

      if (csrError) {
        console.error('Error fetching CSRs:', csrError);
        return;
      }

      if (!csrs || csrs.length === 0) {
        console.log('No CSRs assigned to company:', campaign.company_id);
        return;
      }

      console.log('Found CSRs:', csrs);

      // Only create notifications if there are CSRs assigned
      if (csrs && csrs.length > 0) {
        // Create notifications for each CSR
        const notifications = csrs.map(csr => ({
          user_id: csr.csr_id,
          type: 'csv_upload_ready',
          title: 'CSV Upload Ready for Processing',
          message: `Campaign "${campaign.name}" has uploaded a CSV file: ${fileName} (${leadCount} leads)`,
          data: {
            campaign_id: campaignId,
            campaign_name: campaign.name,
            file_name: fileName,
            lead_count: leadCount,
            action_url: '/csr/campaigns'
          },
          priority: 'medium',
          read: false
        }));

        console.log('Creating notifications:', notifications);

        const { error: notifError } = await supabase
          .from('notifications')
          .insert(notifications);

        if (notifError) {
          console.error('Error inserting notifications:', notifError);
        } else {
          console.log('CSR notifications created successfully');
        }
      } else {
        console.log('No CSRs to notify');
      }
    } catch (error) {
      console.error('Error creating CSR notification:', error);
      // Don't fail the upload if notification fails
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Banner */}
      {uploadSuccess && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                CSV Upload Successful!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Your CSV file has been uploaded and attached to this campaign.</p>
                <p className="mt-1">
                  • File: {file?.name}<br/>
                  • Leads: {rowCount} rows<br/>
                  • Status: Ready for CSR processing
                </p>
                <p className="mt-2 font-medium">
                  Your CSR has been notified and will begin processing your leads shortly.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner (show when not uploaded yet) */}
      {!uploadSuccess && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <p className="text-blue-700">
            Upload a CSV file containing your lead list. Our CSR team will use this list to execute your campaign.
          </p>
        </div>
      )}

      {/* File Upload */}
      <div className={`bg-white p-6 rounded-lg shadow ${uploadSuccess ? 'opacity-75' : ''}`}>
        <h3 className="text-lg font-semibold mb-4">Upload CSV File</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              disabled={uploadSuccess}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {file && (
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm font-medium">Selected File:</p>
              <p className="text-sm text-gray-600">{file.name}</p>
              <p className="text-sm text-gray-600">
                Size: {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <p className="text-sm text-gray-600">
                Rows: {rowCount}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CSV Preview */}
      {preview.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">CSV Preview (First 5 rows)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {Object.keys(preview[0]).map(header => (
                    <th
                      key={header}
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value: any, idx) => (
                      <td
                        key={idx}
                        className="px-4 py-2 whitespace-nowrap text-sm text-gray-900"
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CSV Format Guidelines */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <h4 className="font-medium mb-2">CSV Format Guidelines:</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• First row should contain column headers</li>
          <li>• Minimum required columns: Name, LinkedIn URL</li>
          <li>• Recommended columns: Name, Company, Title, Email, LinkedIn URL</li>
          <li>• Maximum file size: 50MB</li>
          <li>• Supported format: CSV (comma-separated values)</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        {onBack && (
          <button
            onClick={onBack}
            disabled={uploadSuccess}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
        )}
        {!uploadSuccess ? (
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        ) : (
          <div className="ml-auto flex items-center space-x-3">
            <span className="flex items-center text-green-600">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              CSV Upload Complete
            </span>
            <button
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              onClick={() => {
                // Navigate to next step by triggering parent navigation
                // The parent should handle moving to step 5 (Messages)
                if (onComplete) {
                  onComplete({
                    type: 'csv_upload',
                    data: { 
                      message: 'CSV upload completed successfully',
                      shouldAdvance: true  // Signal to advance to next step
                    }
                  });
                }
              }}
            >
              Proceed to Next Step →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}